import JobList from '../../../../pages/admin/jobs/components/JobList';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetJobs, handleGetTemplate } from '~/thunks/job/jobThunk';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { catchError, handleApiError, notify } from '~/utils/helper';
import classNames from 'classnames/bind';
import styles from './CustomerDetail.module.scss';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_TYPES_ORGANIZATION, STATUS_TABS_CUSTOMER_DETAILS } from '~/utils/constants/customers';
import { IPayloadDeleteTasks, ITask } from '~/utils/interface/task';
import { PERMISSIONS } from '~/utils/constants/permission';
import usePermission from '~/hooks/usePermission';
import ActivitiesNotes from './Notes/Notes';
import { ICustomer } from '~/utils/interface/customer';
import { handleGetDetail, handleGetSelectionCustomers } from '~/thunks/customer/customerThunk';
import TaskList from './task/taskList';
import { PlusIcon, TrashIcon } from '~/assets/icons';
import { handleGetTask } from '~/thunks/task/taskThunk';
import { taskActions } from '~/thunks/task/taskSlice';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import EditTaskDialog from '../components/EditTaskDialog';
import { handleGetSelectAccounts, handleGetSelectSalePersons } from '~/thunks/account/accountThunk';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import taskApi from '~/apis/task';
import BasicInfo from './components/BasicInfo/BasicInfo';
import { BaseHeaderTitle } from '~/components/common';

import File from './components/File/File';
import Activity from './Activities/Activities';

import Contact from './contact/Contact';

import SendEmail from './SendEmail/SendEmail';
import EditCustomerDialog from '~/components/customer/edit-customer-dialog/EditCustomerDialog';

const cx = classNames.bind(styles);

const CustomerDetail = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { filter } = useAppSelector((state) => state.job);
  const { t } = useTranslation('admin/customer');
  const permissions = usePermission();
  const { hash } = useLocation();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  const { selectedIds, menuSelectedId, openAlertModalDelete } = useAppSelector((state) => state.task);
  //#endregion Selector

  //#region Declare State
  const [page, setPage] = useState<string>('basicinformation');
  const [customer, setCustomer] = useState<ICustomer>();
  const [refreshCustomer, setRefreshCustomer] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  // States for tasks
  const [openEditTaskDialog, setOpenEditTaskDialog] = useState<boolean>(false);
  const [editTaskDialogMode, setEditTaskDialogMode] = useState<string>('');
  const [task, setTask] = useState<ITask>();
  const [openSendMailDialog, setOpenSendMailDialog] = useState<boolean>(false);
  const tabHeaders = useMemo(() => {
    const tabContent: Array<string> = [];
    STATUS_TABS_CUSTOMER_DETAILS.forEach((item) => {
      if (
        (item === 'basicinformation' && permissions[PERMISSIONS.organization_view]) ||
        (item === 'contacts' && permissions[PERMISSIONS.contact_view]) ||
        (item === 'tasks' && permissions[PERMISSIONS.organizationTask_view]) ||
        (item === 'notes' && permissions[PERMISSIONS.organizationNote_view]) ||
        (item === 'activities' && permissions[PERMISSIONS.organizationActivity_view]) ||
        (item === 'files' && permissions[PERMISSIONS.organizationFile_view]) ||
        (item === 'jobs' && permissions[PERMISSIONS.project_view])
      ) {
        tabContent.push(item);
      }
    });

    return tabContent;
  }, [permissions]);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    dispatch(handleGetSelectAccounts({}));
  }, [dispatch]);
  useEffect(() => {
    dispatch(handleGetSelectSalePersons({ companyAccountRoles: ['salesperson'] }));
  }, [dispatch]);
  useEffect(() => {
    dispatch(handleGetTemplate({ params: { type: 'email' } }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch]);
  useEffect(() => {
    if (id || (id && refreshCustomer)) {
      setRefreshCustomer(false);
      (async () => {
        try {
          const customer = await dispatch(handleGetDetail(id.toString())).unwrap();
          setCustomer(customer);
        } catch (e) {
          handleApiError(e);
        }
      })();
    }
  }, [id, dispatch, refreshCustomer]);
  useEffect(() => {
    dispatch(handleGetSelectionCustomers({ type: 'organization' }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch]);
  // Fetching jobs and customers for the customer details
  useEffect(() => {
    dispatch(handleGetJobs({ ...filter, jobCustomerId: id }))
      .unwrap()
      .catch(() => {
        // handleApiError(e);
      });
  }, [dispatch, filter, id]);

  useEffect(() => {
    if (hash) setPage(hash.slice(1));
  }, [hash]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleOpenEdit = async () => {};

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    navigate(`#${newValue}`);
  };

  // Task handlers
  const handleOpenEditTask = async (id: string) => {
    try {
      const response = await dispatch(handleGetTask(id)).unwrap();

      setTask(response);
      setEditTaskDialogMode(DIALOG_MODE_EDIT);
      setOpenEditTaskDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleClickTask = () => {
    setEditTaskDialogMode(DIALOG_MODE_CREATE);
    setOpenEditTaskDialog(true);
  };

  // Contact handlers (similar to tasks)

  const handleCloseDialog = async (open: boolean) => {
    setOpenSendMailDialog(open);
  };

  const handleClickSendMail = async () => {
    setOpenSendMailDialog(true);
  };

  // Adjust handleSetOpen for both task and contact dialogs
  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    if (openEditTaskDialog) setOpenEditTaskDialog(open);

    setEditTaskDialogMode('');
    setOpenEditTaskDialog(open);
    setTask(undefined);
    if (needRefreshData) dispatch(taskActions.changeNeedRefreshData(true));
  };
  const handleSetOpenEditInfo = async (open: boolean, needRefreshData?: boolean) => {
    setOpenEditDialog(open);
    needRefreshData && setRefreshCustomer(needRefreshData);
  };
  const openAlertDeleteModal = useCallback(() => {
    dispatch(taskActions.changeOpenModalDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(taskActions.changeMenuSelectId(undefined));
    dispatch(taskActions.changeOpenModalDelete(false));
  }, [dispatch]);

  const handleOpenEditInfo = async () => {
    const response = await dispatch(handleGetDetail(id??'')).unwrap();
    setCustomer(response);
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(true);
  };
  const onDeleteSelectedTasks = useCallback(async () => {
    let payloadDeleteAccounts: IPayloadDeleteTasks =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await taskApi.deleteTasks(payloadDeleteAccounts);
      notify('success', t('deleteSuccessMessage', { ns: 'admin/common' }));
      dispatch(taskActions.changeNeedRefreshData(true));
      dispatch(taskActions.changeOpenModalDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  const renderTabContent = () => {
    switch (page) {
      case 'basicinformation':
        return <BasicInfo data={customer} openEditDialog={handleOpenEditInfo} />;
      case 'contacts':
        return id && permissions[PERMISSIONS.contact_view] && <Contact id={id} customer={customer} />;

      case 'notes':
        return id && permissions[PERMISSIONS.organizationNote_view] ? (
          <ActivitiesNotes id={id} isContact={false} />
        ) : null;

      case 'tasks':
        return id && permissions[PERMISSIONS.contactTask_view] ? (
          <TaskList id={id} onOpenEdit={handleOpenEditTask} />
        ) : null;
      case 'files':
        return id && permissions[PERMISSIONS.organizationFile_view] && <File data={customer} id={id} />;
      case 'activities':
        return id && permissions[PERMISSIONS.organizationActivity_view] ? <Activity id={id} /> : null;
      case 'jobs':
        return (
          permissions[PERMISSIONS.project_view] && (
            <JobList onOpenEdit={handleOpenEdit} hideMenuButton={true} hideColumns={['customer']} />
          )
        );

      default:
        return null;
    }
  };
  //#endregion Handle Function

  return (
    <>
      <Box className='w-full h-full'>
        <BaseHeaderTitle className='mb-[20px]' title={customer?.name || t('title')} />
        <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center mb-[50px]'>
          <Box className={cx('tab-list-container')}>
            <Tabs value={page} onChange={handleChange}>
              {tabHeaders.map((status: string) => (
                <Tab
                  value={status}
                  key={status}
                  label={t(`customerHeader.${status}`)}
                  className={cx('tab-item-container')}
                />
              ))}
            </Tabs>
          </Box>
          {permissions[PERMISSIONS.send_email] && (
            <Button
              className='w-[123px] h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] mt-[10px] ml-[10px]'
              variant='contained'
              disableElevation
              onClick={handleClickSendMail}
            >
              {t('sendMail')}
            </Button>
          )}
        </Box>
        <SendEmail open={openSendMailDialog} setOpen={handleCloseDialog} id={id} />
        {page === 'tasks' && (
          <Box className='flex justify-end'>
            {permissions[PERMISSIONS.organizationTask_delete] && (
              <Button
                className={`${cx(
                  { 'visibility: visible': selectedIds.length > 0 },
                  { 'visibility: hidden': selectedIds.length <= 0 }
                )} w-[107px] h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] mt-[10px]`}
                onClick={openAlertDeleteModal}
                variant='contained'
                color='error'
                disableElevation
                startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
              >
                {t('delete')}
              </Button>
            )}
            {permissions[PERMISSIONS.organizationTask_create] && (
              <Button
                className='w-[107px] h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] mt-[10px] ml-[10px]'
                variant='contained'
                disableElevation
                startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
                onClick={handleClickTask}
              >
                {t('addNew')}
              </Button>
            )}
          </Box>
        )}
        {renderTabContent()}

        <EditTaskDialog
          open={openEditTaskDialog}
          mode={editTaskDialogMode}
          setOpen={handleSetOpen}
          data={task}
          customer={customer}
        />
        <EditCustomerDialog
          open={openEditDialog}
          setOpen={handleSetOpenEditInfo}
          mode={editDialogMode}
          data={customer}
          type={CUSTOMER_TYPES_ORGANIZATION}
        />
        <ModalAlertDelete
          open={openAlertModalDelete}
          description={t('alertDescriptionTask')}
          onCancel={closeAlertDeleteModal}
          onConfirm={onDeleteSelectedTasks}
        />
      </Box>
    </>
  );
};
export default CustomerDetail;
